import React from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import { colors, radii, screenPadding, spacing } from '../../theme';
import Skeleton, { useShimmer } from './Skeleton';

/**
 * One skeleton per tab, shaped like the page it stands in for.
 *
 * Shaped, not generic. A column of identical grey bars covers the gap but tells
 * you nothing, and when the real page lands nothing is where the grey was - the
 * fill-in becomes its own jump. These trace the actual layout: the hero and the
 * category grid on Home, the cards on Bookings, the rows on Chat, the dark
 * header and the stats strip on the account.
 *
 * They are drawn on the page's own background, opaque, because they cover the
 * real content while it mounts rather than replacing it.
 */

export type TabSkeletonKind = 'home' | 'bookings' | 'chat' | 'profile';

/** A row of square tiles, which three of the four pages open with. */
function Tiles({ pulse, count }: { pulse: Animated.Value; count: number }) {
  return (
    <View style={styles.tiles}>
      {Array.from({ length: count }, (_, i) => (
        <View key={i} style={styles.tile}>
          <Skeleton pulse={pulse} height={72} radius={radii.md} />
          <Skeleton pulse={pulse} height={9} width="70%" />
        </View>
      ))}
    </View>
  );
}

function TabSkeleton({ kind }: { kind: TabSkeletonKind }) {
  const pulse = useShimmer();

  if (kind === 'bookings') {
    return (
      <View style={styles.root}>
        <View style={styles.headRow}>
          <Skeleton pulse={pulse} height={20} width="45%" />
          <Skeleton pulse={pulse} height={22} width={92} radius={radii.pill} />
        </View>

        <Skeleton pulse={pulse} height={13} width="30%" style={styles.gap} />
        <Tiles pulse={pulse} count={4} />

        <Skeleton
          pulse={pulse}
          height={120}
          radius={radii.lg}
          style={styles.gap}
        />

        <View style={styles.chips}>
          {[64, 84, 88, 82].map((w, i) => (
            <Skeleton key={i} pulse={pulse} height={30} width={w} radius={radii.pill} />
          ))}
        </View>

        {[0, 1].map(i => (
          <Skeleton
            key={i}
            pulse={pulse}
            height={104}
            radius={radii.lg}
            style={styles.card}
          />
        ))}
      </View>
    );
  }

  if (kind === 'chat') {
    return (
      <View style={styles.root}>
        <Skeleton pulse={pulse} height={118} radius={radii.lg} />
        <Skeleton
          pulse={pulse}
          height={44}
          radius={radii.md}
          style={styles.gap}
        />

        <View style={styles.chips}>
          {[54, 68, 92, 84].map((w, i) => (
            <Skeleton key={i} pulse={pulse} height={30} width={w} radius={radii.pill} />
          ))}
        </View>

        {[0, 1, 2, 3, 4].map(i => (
          <View key={i} style={styles.row}>
            <Skeleton pulse={pulse} height={42} width={42} round />
            <View style={styles.rowText}>
              <Skeleton pulse={pulse} height={13} width="45%" />
              <Skeleton pulse={pulse} height={11} width="80%" />
            </View>
          </View>
        ))}
      </View>
    );
  }

  if (kind === 'profile') {
    return (
      <View style={styles.root}>
        {/* The account opens on a dark band, so its skeleton does too - a pale
            one would flash white before the header lands. */}
        <View style={styles.dark}>
          <Skeleton pulse={pulse} height={22} width={120} style={styles.centre} />
          <View style={styles.identity}>
            <Skeleton pulse={pulse} height={62} width={62} round />
            <View style={styles.rowText}>
              <Skeleton pulse={pulse} height={18} width="60%" />
              <Skeleton pulse={pulse} height={12} width="45%" />
            </View>
          </View>
        </View>

        <Skeleton
          pulse={pulse}
          height={66}
          radius={radii.lg}
          style={styles.stats}
        />

        {[0, 1].map(i => (
          <Skeleton
            key={i}
            pulse={pulse}
            height={i === 0 ? 210 : 168}
            radius={radii.lg}
            style={styles.card}
          />
        ))}
      </View>
    );
  }

  // ------------------------------------------------------------------- home
  return (
    <View style={styles.rootFlush}>
      <Skeleton pulse={pulse} height={300} radius={0} />

      <View style={styles.body}>
        <View style={styles.headRow}>
          <Skeleton pulse={pulse} height={17} width="35%" />
          <Skeleton pulse={pulse} height={13} width={64} />
        </View>

        <Tiles pulse={pulse} count={3} />
        <Tiles pulse={pulse} count={3} />

        <Skeleton
          pulse={pulse}
          height={92}
          radius={radii.lg}
          style={styles.gap}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    padding: screenPadding,
    backgroundColor: colors.background,
  },
  /* Home's hero runs to the edges, so its skeleton cannot be inset. */
  rootFlush: {
    flex: 1,
    backgroundColor: colors.background,
  },
  body: {
    padding: screenPadding,
  },

  headRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  gap: {
    marginTop: spacing.lg,
  },
  card: {
    marginTop: spacing.md,
  },

  tiles: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  tile: {
    flex: 1,
    gap: spacing.sm,
  },

  chips: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },

  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  rowText: {
    flex: 1,
    gap: spacing.sm,
  },

  dark: {
    margin: -screenPadding,
    padding: screenPadding,
    paddingBottom: spacing.xxxl,
    backgroundColor: colors.surfaceInverse,
  },
  centre: {
    alignSelf: 'center',
  },
  identity: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg,
    marginTop: spacing.xl,
  },
  stats: {
    marginTop: spacing.xl,
  },
});

export default TabSkeleton;
